import React from 'react';
import CollapsiblePanel from './CollapsiblePanel';
import ColorFillField, { ColorFillPreviewBar } from './ColorFillField';
import { normalizeColorFill } from '../utils/colorFillUtils';

const inputClass =
  'w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40';

const selectClass = `${inputClass} pr-9 cursor-pointer appearance-none`;

function Field({ label, children, hint }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-foreground/80">{label}</span>
      {children}
      {hint ? <span className="text-[11px] text-muted-foreground block">{hint}</span> : null}
    </label>
  );
}

function BackgroundPreview({ bg }) {
  if (bg?.type === 'image' && bg?.imageUrl) {
    return (
      <div
        className="h-8 w-28 rounded-md border border-border shrink-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${bg.imageUrl})` }}
        title="Aperçu"
      />
    );
  }
  const fill = bg?.fill ?? bg?.color ?? '#ffffff';
  return (
    <ColorFillPreviewBar
      fill={fill}
      fallback="#ffffff"
      variant={normalizeColorFill(fill).mode === 'gradient' ? 'header' : 'compact'}
    />
  );
}

/** Réglages fond (couleur/dégradé ou image) — page, section ou composant. */
export default function BackgroundFields({
  value,
  onChange,
  showEnableToggle = false,
  label = 'Fond',
  defaultOpen = false,
}) {
  const bg = value || {
    type: 'color',
    fill: { mode: 'solid', color: '#ffffff', alpha: 1 },
    imageUrl: '',
    imageSize: 'cover',
  };

  const patch = (next) => onChange({ ...bg, ...next });

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => patch({ type: 'image', imageUrl: reader.result });
    reader.readAsDataURL(file);
  };

  const colorFill = bg.fill ?? bg.color ?? '#ffffff';
  const disabled = showEnableToggle && !bg.enabled;

  return (
    <CollapsiblePanel
      title={label}
      defaultOpen={defaultOpen}
      preview={<BackgroundPreview bg={disabled ? { type: 'color', color: '#ffffff' } : bg} />}
    >
      {showEnableToggle ? (
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={bg.enabled === true}
            onChange={(e) => patch({ enabled: e.target.checked })}
          />
          Fond propre
        </label>
      ) : null}

      {(!showEnableToggle || bg.enabled) && (
        <>
          <Field label="Type de fond">
            <select
              className={inputClass}
              value={bg.type || 'color'}
              onChange={(e) => patch({ type: e.target.value })}
            >
              <option value="color">Couleur / dégradé</option>
              <option value="image">Image</option>
            </select>
          </Field>

          {(bg.type || 'color') === 'color' ? (
            <ColorFillField
              value={colorFill}
              onChange={(fill) => patch({ fill: normalizeColorFill(fill), color: undefined })}
              fallback="#ffffff"
              nested
            />
          ) : null}

          {bg.type === 'image' && (
            <>
              <ColorFillField
                label="Couleur sous l'image"
                value={colorFill}
                onChange={(fill) => patch({ fill: normalizeColorFill(fill), color: undefined })}
                fallback="#ffffff"
              />
              <Field label="Image (URL)">
                <input
                  className={inputClass}
                  value={bg.imageUrl || ''}
                  onChange={(e) => patch({ imageUrl: e.target.value })}
                  placeholder="https://..."
                />
              </Field>
              <Field label="Importer une image">
                <input type="file" accept="image/*" onChange={handleFile} className="text-sm" />
              </Field>
              <Field label="Affichage">
                <select
                  className={inputClass}
                  value={bg.imageSize || 'cover'}
                  onChange={(e) => patch({ imageSize: e.target.value })}
                >
                  <option value="cover">Couvrir (cover)</option>
                  <option value="contain">Contenir (contain)</option>
                </select>
              </Field>
            </>
          )}
        </>
      )}
    </CollapsiblePanel>
  );
}

export { Field, inputClass, selectClass };
